import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import { API_URL } from '../config';
import { FaHeart, FaRegHeart, FaTimes, FaRecycle, FaChevronLeft, FaArrowLeft } from 'react-icons/fa';
import NumberSelectionDialog from '../components/NumberSelectionDialog';

const ImageView = () => {
  const { imageId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated, user, loginWithRedirect } = useAuth0();
  
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [showNumberSelection, setShowNumberSelection] = useState(false);

  // Fetch image data
  useEffect(() => {
    const fetchImage = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/api/images/public/${imageId}`);
        
        if (!response.ok) {
          throw new Error('Image not found');
        }
        
        const data = await response.json();
        setImage(data);
        setIsFavorited(data.isFavorited || false);
        setFavoriteCount(data.favoriteCount || 0);
      } catch (err) {
        console.error('Error fetching image:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (imageId) {
      fetchImage();
    }
  }, [imageId]);

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: `/image/${imageId}` }
      });
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const method = isFavorited ? 'DELETE' : 'POST';
      
      const response = await fetch(`${API_URL}/api/favorites/${imageId}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to update favorite');
      }

      setIsFavorited(!isFavorited);
      setFavoriteCount(prev => isFavorited ? prev - 1 : prev + 1);
    } catch (error) {
      console.error('Error updating favorite:', error);
    }
  };

  const handleRecreate = () => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: `/image/${imageId}` }
      });
      return;
    }
    setShowNumberSelection(true);
  };

  const handleNumberSelected = (numImages) => {
    setShowNumberSelection(false);
    // Navigate to create page with recreation params
    // We pass the source image ID and number of images, but NOT the prompt
    navigate(`/create?recreate=${imageId}&count=${numImages}`);
  };

  const handleClose = () => {
    // Go back or to gallery
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/gallery');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div 
          className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  if (error || !image) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white">
        <p className="text-[24px] font-['VT323'] mb-4">Image not found</p>
        <Link 
          to="/gallery" 
          className="text-[18px] font-['VT323'] text-white/60 hover:text-white transition-colors"
        >
          ← Back to Gallery
        </Link>
      </div>
    );
  }

  const displayUrl = image.thumbnailUrl || image.s3Url || image.url;

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-4">
        {/* Back button */}
        <button
          onClick={handleClose}
          className="flex items-center gap-2 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <FaArrowLeft className="w-5 h-5 text-white" />
        </button>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {/* Like button */}
          <motion.button
            onClick={handleToggleFavorite}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            {isFavorited ? (
              <FaHeart className="w-5 h-5 text-red-500" />
            ) : (
              <FaRegHeart className="w-5 h-5 text-white" />
            )}
            {favoriteCount > 0 && (
              <span className={`text-[14px] font-['VT323'] ${isFavorited ? 'text-red-500' : 'text-white'}`}>
                {favoriteCount}
              </span>
            )}
          </motion.button>

          {/* Recreate button */}
          <motion.button
            onClick={handleRecreate}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FF9F]/20 hover:bg-[#00FF9F]/30 text-[#00FF9F] transition-colors"
          >
            <FaRecycle className="w-5 h-5" />
            <span className="font-['VT323'] text-[18px]">Recreate</span>
          </motion.button>
        </div>
      </div>

      {/* Image */}
      <div className="flex-1 flex items-center justify-center p-4">
        <motion.img
          src={displayUrl}
          alt=""
          className="max-w-full max-h-full object-contain rounded-xl"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Bottom info - NO PROMPT SHOWN */}
      <div className="px-4 py-6 text-center">
        <p className="text-white/40 font-['VT323'] text-[14px]">
          From Blatant Space
        </p>
        <p className="text-white/60 font-['VT323'] text-[16px] mt-1">
          {image.createdAt && new Date(image.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </p>
      </div>

      {/* Number Selection Dialog */}
      <AnimatePresence>
        {showNumberSelection && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80">
            <NumberSelectionDialog
              isOpen={showNumberSelection}
              onClose={() => setShowNumberSelection(false)}
              onSelect={handleNumberSelected}
            />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImageView;
